/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  ProcessorWrapper,
  type BackgroundOptions,
} from "@livekit/track-processors";
import { type ProcessorOptions, type Track } from "livekit-client";
import { logger } from "matrix-js-sdk/lib/logger";

import { frameDrawSize } from "./backgroundFrameSync";

interface FallbackDraw {
  frameCallback?: (frame: VideoFrame) => void;
  displayCanvas?: HTMLCanvasElement;
  renderContext?: CanvasRenderingContext2D | null;
  transformer: {
    transform: (
      frame: VideoFrame,
      controller: TransformStreamDefaultController<VideoFrame>,
    ) => void | Promise<void>;
  };
}

/**
 * The still image is drawn behind the camera. On the fallback path LiveKit
 * paints that result into the canvas from startup and stretches it. This
 * adopts the camera frame's size and copies it one-to-one.
 */
export class StillBackgroundProcessor extends ProcessorWrapper<BackgroundOptions> {
  public override async init(
    opts: ProcessorOptions<Track.Kind>,
  ): Promise<void> {
    await super.init(opts);
    adoptCameraFrameSize(this);
  }

  public override async restart(
    opts: ProcessorOptions<Track.Kind>,
  ): Promise<void> {
    await super.restart(opts);
    adoptCameraFrameSize(this);
  }
}

function adoptCameraFrameSize(
  processor: ProcessorWrapper<BackgroundOptions>,
): void {
  const surface = processor as unknown as FallbackDraw;
  if (
    surface.displayCanvas === undefined ||
    surface.frameCallback === undefined
  ) {
    return;
  }
  surface.frameCallback = (frame: VideoFrame): void => {
    const controller = {
      enqueue: (processed: VideoFrame): void => {
        const canvas = surface.displayCanvas;
        const context = surface.renderContext;
        if (canvas === undefined || context == null) {
          processed.close();
          return;
        }
        const size = frameDrawSize(
          processed.displayWidth,
          processed.displayHeight,
          canvas.width,
          canvas.height,
        );
        if (size.resize) {
          canvas.width = size.width;
          canvas.height = size.height;
        }
        context.drawImage(processed, 0, 0, size.width, size.height);
        processed.close();
      },
    } as TransformStreamDefaultController<VideoFrame>;
    try {
      void Promise.resolve(
        surface.transformer.transform(frame, controller),
      ).catch((error: unknown) => {
        logger.warn("Failed to draw the call background", error);
      });
    } catch (error) {
      frame.close();
      logger.warn("Failed to draw the call background", error);
    }
  };
}
